import { useEffect, useMemo, useState } from "react";
import {
  useGetPublicAgainstShopQuery,
  useGetAllConnectedQuery,
} from "../features/connection/connectionApiSlice";

const formatTabTitle = (key) =>
  key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1");

const idStr = (v) => {
  if (v == null || v === "" || v === "null" || v === "undefined") return null;
  if (typeof v === "object") return String(v._id || v.id || "") || null;
  const s = String(v);
  return s && s !== "null" && s !== "undefined" ? s : null;
};

const normBT = (bt) =>
  String(bt || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const isMarketerBT = (bt) => {
  const n = normBT(bt);
  return (
    n === "marketerprofile" ||
    n === "marketer" ||
    n === "marketershop" ||
    n === "approvedmarketers"
  );
};

/** Shape connectedBT tiles so Timeline / usePostState can render Connect status */
const mapConnectedItemToTabProfile = (item, groupBT) => {
  const uid = idStr(item?.userid) || idStr(item?._id);
  const sid = idStr(item?.shopid);
  const bt = item?.linkBusinessType || item?.businessType || groupBT;
  const isGP = normBT(bt) === "generalpublic" || (!sid && !isMarketerBT(bt));
  return {
    _id: sid || uid,
    userid: uid,
    shopid: sid || null,
    businessType: isMarketerBT(bt)
      ? "marketerProfile"
      : isGP
        ? "generalPublic"
        : bt || groupBT,
    generalPublic: isGP && !isMarketerBT(bt),
    fullname: item?.fullname || item?.shopname || "Profile",
    shopname: item?.shopname || item?.fullname || "Profile",
    image: item?.image || item?.imageUrl || "N/A",
    imageUrl: item?.imageUrl || item?.image || "N/A",
    _marketerDocId: item?._marketerDocId || null,
    asMarketerProfile: item?.asMarketerProfile === true,
    // Mark already-connected so UI can show Connected without re-send
    _fromConnected: true,
  };
};

/**
 * Builds timeline profile tabs from:
 * 1) public-against-shop (profiles still available to connect — right side)
 * 2) already-connected profiles injected into the matching BT tab
 *    (so generalPublic / shop tabs still show connections after accept)
 * 3) empty tab shells for connected BTs that have no public suggestions yet
 */
export const useBusinessProfileTabs = ({
  shopid,
  userid,
  businessType,
}) => {
  const [tabsData, setTabsData] = useState([]);
  const [activeTab, setActiveTab] = useState("");

  const { data: publicRes, isLoading: publicLoading } =
    useGetPublicAgainstShopQuery(
      { businessType, shopid, userid },
      {
        skip: !shopid || !userid || !businessType,
        refetchOnMountOrArgChange: true,
      },
    );

  const { data: connectedRes, isLoading: connectedLoading } =
    useGetAllConnectedQuery(
      { shopid, userid },
      { skip: !shopid || !userid, refetchOnMountOrArgChange: true },
    );

  const loading = publicLoading || connectedLoading;

  // Ids that must never appear as "Connect" suggestions (already connected)
  const connectedHideIds = useMemo(() => {
    const ids = new Set();
    const shopIdStr = idStr(shopid);
    const me = idStr(userid);

    (connectedRes?.connectedBT || []).forEach((group) => {
      (group?.items || []).forEach((item) => {
        const uid = idStr(item?.userid);
        const sid = idStr(item?.shopid);
        if (uid) ids.add(uid);
        if (sid) ids.add(sid);
      });
    });

    (connectedRes?.connected || []).forEach((conn) => {
      const senderId = idStr(conn?.senderid);
      const receiverId = idStr(conn?.recieverid);
      const senderShop = idStr(conn?.sendershopid);
      const receiverShop = idStr(conn?.recievershopid);
      const senderCtx = idStr(conn?.senderShopContextId);
      const receiverCtx = idStr(conn?.recieverShopContextId);
      const involvesShop =
        (shopIdStr &&
          (senderShop === shopIdStr ||
            receiverShop === shopIdStr ||
            senderCtx === shopIdStr ||
            receiverCtx === shopIdStr)) ||
        senderId === me ||
        receiverId === me;
      if (!involvesShop) return;

      const iAmSender =
        senderId === me || (shopIdStr && senderShop === shopIdStr);
      if (iAmSender) {
        if (receiverId) ids.add(receiverId);
        if (receiverShop) ids.add(receiverShop);
      } else {
        if (senderId) ids.add(senderId);
        if (senderShop) ids.add(senderShop);
      }
    });

    return ids;
  }, [connectedRes, shopid, userid]);

  /** Connected profiles grouped by canonical BT for right-side tabs */
  const connectedByBT = useMemo(() => {
    const map = new Map(); // bt -> profiles[]
    const push = (bt, profile) => {
      if (!bt || !profile) return;
      const key = isMarketerBT(bt)
        ? "marketerProfile"
        : normBT(bt) === "generalpublic"
          ? "generalPublic"
          : bt;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(profile);
    };

    (connectedRes?.connectedBT || []).forEach((group) => {
      const gBT = group?.businessType || "generalPublic";
      (group?.items || []).forEach((item) => {
        if (
          !item ||
          item.isPlaceholder ||
          (!item.userid && !item.shopid && !item._id)
        ) {
          return;
        }
        push(gBT, mapConnectedItemToTabProfile(item, gBT));
      });
    });

    // Flat connected fallback (when connectedBT omitted a GP group).
    // getAllConnected?shopid=… already scopes `connected` to this shop, so
    // person-level counterparts (blank other shop) belong here even when URL
    // nanoid ≠ Mongo id stored on the row.
    const shopIdStr = idStr(shopid);
    const me = idStr(userid);
    (connectedRes?.connected || []).forEach((row) => {
      const senderShop = idStr(row?.sendershopid);
      const receiverShop = idStr(row?.recievershopid);
      const senderCtx = idStr(row?.senderShopContextId);
      const receiverCtx = idStr(row?.recieverShopContextId);
      const iAmSender =
        idStr(row?.senderid) === me ||
        (shopIdStr &&
          (senderShop === shopIdStr || senderCtx === shopIdStr));
      const iAmReceiver =
        idStr(row?.recieverid) === me ||
        (shopIdStr &&
          (receiverShop === shopIdStr || receiverCtx === shopIdStr));
      if (!iAmSender && !iAmReceiver) return;

      const otherUserId = iAmSender
        ? idStr(row?.recieverid)
        : idStr(row?.senderid);
      const otherShopId = iAmSender ? receiverShop : senderShop;
      // Person counterpart on an already shop-scoped payload
      const otherIsPerson = !otherShopId;
      const involvesThisShop =
        !shopIdStr ||
        senderShop === shopIdStr ||
        receiverShop === shopIdStr ||
        senderCtx === shopIdStr ||
        receiverCtx === shopIdStr ||
        otherIsPerson;
      if (!involvesThisShop) return;

      const otherBT = iAmSender
        ? row?.businessType
        : row?.senderBusinessType;
      const otherUser = iAmSender ? row?.receiverUser : row?.senderUser;
      const marketingOrigin =
        row?.asMarketerProfile === true ||
        row?.fromMarketerProfile === true ||
        isMarketerBT(row?.senderBusinessType) ||
        isMarketerBT(row?.businessType);

      let tabBT = "generalPublic";
      if (marketingOrigin && !otherShopId) {
        tabBT = "marketerProfile";
      } else if (otherShopId) {
        tabBT = otherBT || businessType || "generalPublic";
      } else if (normBT(otherBT) === "generalpublic" || !otherBT) {
        tabBT = "generalPublic";
      } else if (isMarketerBT(otherBT)) {
        tabBT = "marketerProfile";
      } else {
        tabBT = "generalPublic";
      }

      // Prefer connectedBT tiles when present for this user
      const existing = map.get(tabBT) || [];
      if (existing.some((p) => idStr(p.userid) === otherUserId)) return;

      push(
        tabBT,
        mapConnectedItemToTabProfile(
          {
            userid: otherUserId,
            shopid: otherShopId,
            shopname: otherUser?.fullname || "Profile",
            fullname: otherUser?.fullname,
            image: otherUser?.imageUrl || otherUser?.image,
            imageUrl: otherUser?.imageUrl || otherUser?.image,
            asMarketerProfile: row?.asMarketerProfile === true,
          },
          tabBT,
        ),
      );
    });

    return map;
  }, [connectedRes, shopid, userid, businessType]);

  const connectedBTKeys = useMemo(
    () => Array.from(connectedByBT.keys()),
    [connectedByBT],
  );

  useEffect(() => {
    if (loading && !publicRes?.data && !connectedRes) return;

    const responseData = publicRes?.data || {};
    const tabMap = new Map();

    // Suggestions only — strip already-connected so Connect is not re-offered
    const stripConnected = (list) =>
      (Array.isArray(list) ? list : []).filter((item) => {
        const pid = idStr(item?._id);
        const uid = idStr(item?.userid) || pid;
        if (pid && connectedHideIds.has(pid)) return false;
        if (uid && connectedHideIds.has(uid)) return false;
        return true;
      });

    Object.keys(responseData).forEach((key) => {
      tabMap.set(key, {
        eventKey: key,
        title: formatTabTitle(key),
        data: stripConnected(responseData[key]),
      });
    });

    // Always keep a General Public tab shell on shop timelines
    if (!tabMap.has("generalPublic")) {
      tabMap.set("generalPublic", {
        eventKey: "generalPublic",
        title: formatTabTitle("generalPublic"),
        data: [],
      });
    }

    // Inject connected profiles into the matching BT tabs (GP, Marketer, shops…)
    connectedByBT.forEach((profiles, key) => {
      if (!tabMap.has(key)) {
        tabMap.set(key, {
          eventKey: key,
          title: formatTabTitle(key),
          data: [],
        });
      }
      const tab = tabMap.get(key);
      const seen = new Set(
        (tab.data || []).map(
          (p) =>
            `${idStr(p?.userid) || idStr(p?._id) || ""}|${idStr(p?.shopid) || "null"}`,
        ),
      );
      const merged = [...(tab.data || [])];
      // Connected first so the tab clearly shows accepted connections
      const connectedFirst = [];
      for (const p of profiles) {
        const k = `${idStr(p?.userid) || idStr(p?._id) || ""}|${idStr(p?.shopid) || "null"}`;
        if (!k.startsWith("|") && seen.has(k)) continue;
        if (k === "|null") continue;
        seen.add(k);
        connectedFirst.push(p);
      }
      tab.data = [...connectedFirst, ...merged];
    });

    // Keep tab shells for any remaining connected BTs
    connectedBTKeys.forEach((key) => {
      if (!tabMap.has(key)) {
        tabMap.set(key, {
          eventKey: key,
          title: formatTabTitle(key),
          data: [],
        });
      }
    });

    const dynamicTabs = Array.from(tabMap.values()).sort((a, b) => {
      if (a.eventKey === "generalPublic") return -1;
      if (b.eventKey === "generalPublic") return 1;
      if (a.eventKey === "marketerProfile") return -1;
      if (b.eventKey === "marketerProfile") return 1;
      return a.title.localeCompare(b.title);
    });

    setTabsData(dynamicTabs);
    setActiveTab((prev) => {
      if (
        prev &&
        prev !== "home" &&
        dynamicTabs.some((t) => t.eventKey === prev)
      ) {
        return prev;
      }
      return dynamicTabs[0]?.eventKey || "home";
    });
  }, [
    publicRes,
    connectedRes,
    connectedBTKeys,
    connectedByBT,
    connectedHideIds,
    loading,
  ]);

  return {
    tabsData,
    activeTab,
    setActiveTab,
    loading,
    handleTabSelect: setActiveTab,
    getActiveTabData: () =>
      tabsData.find((tab) => tab.eventKey === activeTab)?.data || [],
  };
};
